// CUSTODY — DEPOSITS & ADDRESSES (his custody redesign, yes on 2 Nov 2026; the drawings s3, s4, s4b, s5).
//
// One register: When · Customer · Amount · KYT · State, the action at the row's right where one is his; filters All · Needs
// you · Held · Addresses (the deposit addresses, issued and tagged, in the same place). An opened deposit is a
// sheet: its custody facts, then its KYT beside where it was routed (components/v2/custody/DepositKyt.tsx), and its one
// action — sign its sweep, or open the desk window.
//
// KEPT FROM today's deposits: the read and its 3 s / 20 s pace (fast while a sweep is in motion); the pipeline's stage words;
// StartSigning (the window as the desk holds it); a ready card's link to its review; the lists as information; "unrated never
// sweeps; rated money routes by its rating" — the screen adds no gate.
//
// KYT routes a deposit to Hot (clean) or Merchant Hot (exposed) — never to Flagged (the wallet session, 2 Nov 2026); a status the
// data does not name is drawn plainly, never guessed. RELEASE (N-099) is pending his decision: its place is the opened
// deposit's footer, no button until the agreed fields land.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { custody as api, type DepositRow } from '../../../lib/api';
import { shortAddress } from '../../../lib/custody-format';
import { fmtStamp } from '../../../lib/format';
import { cardReady, readIsCurrent } from '../../../lib/custody-screen';
import { readState } from '../../../lib/read-state';
import { Panel, Register, SkeletonRows, Notice, EmptyState, StateLine, DetailSheet, SearchBar, TAP, whenColumn } from '../../../components/v2/kit';
import { MoreDetails } from '../../../components/MoreDetails';
import { Pill } from '../../../components/Pill';
import { CustodyHead, CustodyTabs, CustodyActions, Figure, StagePill } from '../../../components/v2/custody/parts';
import { STATUS_LINE, kytNotClean, stepsOf, sweepSteps, cardPath, IN_MOTION, waitsOnHim, StartSigning } from '../../../components/v2/custody/deposit-words';
import { DepositKyt, LevelWord, Line, Lookalike, lookalikeOf } from '../../../components/v2/custody/DepositKyt';
import { DepositAddressesV2 } from '../custody-admin/Addresses';
import { ReleaseButton, ReleaseState, canRelease } from '../../../components/v2/custody/Release';

const who = (d: DepositRow) => d.partyName ?? d.partySlug ?? 'Untagged sender';
/** The deposit's quiet second line: its deal, else the sender's address shortened. */
/** The deposit's quiet second line: its deal, when it has one (the sender's address is in the opened deposit). */
const refOf = (d: DepositRow) => d.dealId ?? '';

/** The read every deposit view shares (today's key and pace). */
function useDeposits() {
  return useQuery({
    queryKey: ['custody', 'deposits'],
    queryFn: () => api.deposits(),
    refetchInterval: (query) => (query.state.data?.deposits.some((d) => d.sweep && IN_MOTION.has(d.sweep.status)) ? 3_000 : 20_000),
  });
}

type Show = 'all' | 'needs' | 'held' | 'merchant' | 'addresses';
/** Still waiting on someone (the server's `open`, any age); a row from an older server, without the flag, counts as the day's. */
const isOpen = (d: DepositRow) => d.open ?? true;
const needsHim = (d: DepositRow) => isOpen(d) && (cardReady(d.sweep) || waitsOnHim(d));
const SHOWS: ReadonlyArray<{ id: Show; label: string; has?: (d: DepositRow) => boolean }> = [
  { id: 'all', label: 'All', has: () => true },
  { id: 'needs', label: 'Needs you', has: needsHim },
  { id: 'held', label: 'Held', has: (d) => isOpen(d) && d.status === 'held' },
  // Merchant Hot reads EVERY swept deposit (?status=swept, the newest 200), not the last 24 hours: one older than a day can
  // still be released (N-099).
  { id: 'merchant', label: 'Merchant Hot' },
  { id: 'addresses', label: 'Addresses' },
];

/** KYT's one quiet fact beside its level: the finding's source, else the held deposit's wait. A look-alike's red line says
 *  its own (Lookalike), so the quiet fact steps aside — never the warning twice. */
const kytFact = (d: DepositRow): string | null => lookalikeOf(d) ? null : d.kyt?.view?.finding?.source ?? (d.status === 'held' ? 'asked each minute' : null);

/** KYT in the register: the level, and one quiet fact under it. */
function KytCell({ d }: { d: DepositRow }) {
  const fact = kytFact(d);
  return (
    <span className="block min-w-0">
      <span className="block"><LevelWord d={d} /></span>
      {fact && <span className="mt-1 line-clamp-2 break-words text-meta text-tg-hint">{fact}</span>}
      <Lookalike d={d} className="mt-1" />
    </span>
  );
}

const RELEASE_WORD: Record<string, { word: string; tone: 'neutral' | 'warn' | 'good' | 'bad' }> = {
  queued: { word: 'Release requested', tone: 'neutral' }, activating: { word: 'Release requested', tone: 'neutral' },
  awaiting_approval: { word: 'Release waits', tone: 'warn' }, expired: { word: 'Release waits', tone: 'warn' },
  energizing: { word: 'Sending', tone: 'neutral' }, broadcast: { word: 'Sending', tone: 'neutral' },
  swept: { word: 'Released', tone: 'good' }, failed: { word: 'Release failed', tone: 'bad' },
};
/** The row's state: where its Release stands when it has one, else the pipeline's word. */
function StateCell({ d }: { d: DepositRow }) {
  const r = d.release ? RELEASE_WORD[d.release.status] : undefined;
  return <span className="inline-block whitespace-nowrap">{r ? <Pill tone={r.tone}>{r.word}</Pill> : <StagePill status={d.status} calm={kytNotClean(d)} />}</span>;
}

export function DepositsV2() {
  const q = useDeposits();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { id } = useParams();
  const show = (SHOWS.some((s) => s.id === params.get('show')) ? params.get('show') : 'all') as Show;
  // Newest first, whatever order the read sends.
  const swept = useQuery({ queryKey: ['custody', 'deposits', 'swept'], queryFn: () => api.deposits('swept'), enabled: show === 'merchant' || !!id, refetchInterval: 30_000 });
  const rows = [...(q.data?.deposits ?? [])].sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));
  const merchantRows = [...(swept.data?.deposits ?? [])].filter((d) => d.pool === 'dirty').sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));
  const chosen = SHOWS.find((s) => s.id === show)!;
  // The read carries the open deposits of any age as well as the day's (server 338328fd); an older server, the day's only.
  const anyAge = rows.some((d) => d.open !== undefined);
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const shown = (show === 'merchant' ? merchantRows : chosen.has ? rows.filter(chosen.has) : []).filter((d) => !needle || [who(d), d.amount, `#${d.id}`, d.dealId ?? '', d.fromAddress].some((x) => x.toLowerCase().includes(needle)));
  const open = id ? rows.find((d) => String(d.id) === id) ?? merchantRows.find((d) => String(d.id) === id) ?? (swept.data?.deposits ?? []).find((d) => String(d.id) === id) ?? null : null;
  const setShow = (s: Show) => { const n = new URLSearchParams(params); if (s === 'all') n.delete('show'); else n.set('show', s); setParams(n); };
  const close = () => navigate({ pathname: '/custody/deposits', search: params.toString() ? `?${params}` : '' });

  return (
    <div className="lg:max-w-[1160px]">
      <CustodyHead actions={<CustodyActions primary items={[{ label: 'New address', to: '/custody/deposits/new' }]} />} />
      <CustodyTabs current="deposits" />

      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Show">
        {SHOWS.map((s) => (
          <button key={s.id} type="button" aria-pressed={show === s.id} onClick={() => setShow(s.id)} className={`chip ${show === s.id ? 'chip-on' : ''}`}>
            {s.label}{s.has && q.isSuccess ? <span className="font-mono tabular"> · {rows.filter(s.has).length}</span> : s.id === 'merchant' && swept.isSuccess ? <span className="font-mono tabular"> · {merchantRows.length}</span> : null}
          </button>
        ))}
      </div>

      {show === 'addresses' ? <DepositAddressesV2 embedded /> : q.isError && !q.data ? (
        isCustodySignin(q.error) ? <SigninQuiet error={q.error} /> : <Notice tone="bad" action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t read the deposits.</Notice>
      ) : (
        <>
          {q.isError && !isCustodySignin(q.error) && <Notice action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t refresh — showing the last read.</Notice>}
          {q.isSuccess && rows.length > 0 && <SearchBar value={search} onChange={setSearch} placeholder="Search name, reference or amount" />}
          <Panel flush>
            {!q.data ? <SkeletonRows rows={4} what="the deposits" />
              // An empty claim needs a KNOWN read (Appendix B): after a failed refresh, not "no deposits".
              : show === 'merchant' && !swept.data ? (swept.isError ? <p className="px-4 py-10 text-center text-body text-tg-hint">{isCustodySignin(swept.error) ? 'Waiting for sign-in.' : <>Couldn&apos;t read the swept deposits.</>}</p> : <SkeletonRows rows={3} what="the swept deposits" />)
              : rows.length === 0 && show !== 'merchant' ? (q.isError ? <p className="px-4 py-10 text-center text-body text-tg-hint">Not known — the last read failed.</p> : <EmptyState className="py-10">Nothing open or recent.</EmptyState>)
              : shown.length === 0 ? <EmptyState className="py-10">Nothing here.</EmptyState>
              : (
                <Register<DepositRow>
                  label="Deposits"
                  rows={shown}
                  rowKey={(d) => String(d.id)}
                  href={(d) => `/custody/deposits/${d.id}${params.toString() ? `?${params}` : ''}`}
                  columns={[
                    whenColumn<DepositRow>((d) => d.detectedAt),
                    { key: 'who', head: 'Customer', cell: (d) => (
                      <span className="block min-w-0">
                        <span className="line-clamp-2 break-words font-medium">{who(d)}</span>
                        {refOf(d) && <span className="mt-1 block truncate font-mono text-meta text-tg-hint">{refOf(d)}</span>}
                      </span>
                    ) },
                    { key: 'amount', head: 'Amount', align: 'right', width: 'w-[12.5rem]', cell: (d) => <Figure value={d.amount} asset={d.asset} /> },
                    { key: 'kyt', head: 'KYT', width: 'w-[9.5rem]', cell: (d) => <KytCell d={d} /> },
                    { key: 'state', head: 'State', width: 'w-[9.5rem]', cell: (d) => <StateCell d={d} /> },
                  ]}
                  card={(d) => ({
                    title: <span className="line-clamp-2 break-words">{who(d)}</span>,
                    figure: <Figure value={d.amount} asset={d.asset} />,
                    meta: <span className="block space-y-1"><span className="flex flex-wrap items-center gap-2"><span className="text-tg-hint">KYT</span><LevelWord d={d} />{kytFact(d) && <span className="text-tg-hint">· {kytFact(d)}</span>}</span><Lookalike d={d} />{refOf(d) && <span className="block font-mono">{refOf(d)}</span>}</span>,
                    state: <StateCell d={d} />,
                  })}
                />
              )}
          </Panel>
          {q.isSuccess && rows.length > 0 && <p className="mt-3 text-meta text-tg-hint">{show === 'merchant' ? 'Every swept deposit · newest first' : anyAge ? 'Open of any age, and the last 24 hours' : 'Last 24 hours · newest first'} · Dubai time</p>}
        </>
      )}

      {open && <DepositSheet d={open} onClose={close} known={rows.includes(open) ? readState(q) === 'known' && readIsCurrent(q) : readState(swept) === 'known' && readIsCurrent(swept)} />}
      {id && q.isSuccess && (swept.isSuccess || swept.isError) && !open && (
        <DetailSheet title="Deposit" onClose={close}><EmptyState>Not open or recent.</EmptyState></DetailSheet>
      )}
    </div>
  );
}

/** The opened deposit: the figure and its stage, the custody facts, its KYT and route, the steps behind More, its action. */
/** `known`: the read this deposit came from is current — a money door is offered only from a current read. */
function DepositSheet({ d, onClose, known }: { d: DepositRow; onClose: () => void; known: boolean }) {
  const line = STATUS_LINE[d.status];
  const ready = cardReady(d.sweep);
  const waits = waitsOnHim(d);
  // The sweep's state, unless the footer already says it (StartSigning names the window's own state — never two lines that differ).
  const sweepNow = !waits && d.sweep && sweepSteps(d.sweep).length > 0 ? sweepSteps(d.sweep).at(-1) : null;
  // RELEASE (N-099, pending his decision — the wallet recommends Merchant Hot → Hot for a deposit KYT sent to Merchant Hot): its
  // button and its states take this place once the agreed fields land; until then nothing is offered here for it.
  const footer = canRelease(d) ? <ReleaseButton d={d} known={known} /> : ready ? <Link to={cardPath(d.sweep)} className="btn-primary px-6">Sign the sweep</Link>
    : waits ? <StartSigning />
    : d.partySlug ? null : <Link to="/custody/deposits?show=addresses" className="btn-secondary px-4 text-body">Tag customer</Link>;
  return (
    <DetailSheet title={`Deposit #${d.id}`} onClose={onClose} footer={footer ? <div className="flex flex-wrap justify-end gap-2">{footer}</div> : undefined}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-meta text-tg-hint">Deposit</div>
          <div className="mt-1 font-mono tabular text-figure font-semibold"><Figure value={d.amount} asset={d.asset} /></div>
        </div>
        <StagePill status={d.status} calm={kytNotClean(d)} />
      </div>
      {line && <StateLine className="mt-3" tone={line.tone} more={line.more}>{line.line}</StateLine>}
      <div className="mt-3 empty:hidden"><ReleaseState d={d} /></div>
      {d.sweep?.status === 'activating' && (
        <StateLine className="mt-3" more="A fresh deposit address is activated on the chain before its first sweep — one transaction, about a minute.">Preparing the card — about a minute</StateLine>
      )}

      <h3 className="mb-1 mt-5 text-label font-medium uppercase tracking-wider text-tg-hint">Deposit</h3>
      <Line label="Customer" name><span className="line-clamp-2 break-words">{d.partyName ?? d.partySlug ?? <span className="text-tg-hint">Untagged sender</span>}</span></Line>
      <Line label="Deal">{d.dealId ? <span className="font-mono">{d.dealId}</span> : <span className="text-tg-hint">—</span>}</Line>
      <Line label="Arrived"><span className="font-mono tabular">{fmtStamp(d.detectedAt)}</span></Line>
      <Line label="Came into">Deposit · TRC20</Line>
      {sweepNow && <Line label="Sweep"><span className={d.sweep?.status === 'failed' ? 'text-neg' : ''}>{sweepNow}</span></Line>}
      <Line label="Tx">
        <span className="font-mono">{shortAddress(d.txHash, 4, 4)}</span>
        <a className="tap-44 ml-2 text-tg-link" href={`https://tronscan.org/#/transaction/${d.txHash}`} target="_blank" rel="noreferrer">Tronscan</a>
      </Line>

      <DepositKyt d={d} />

      <div className="mt-4">
        <MoreDetails label="Steps">
          <p className="text-meta text-tg-hint">{stepsOf(d).join(' · ')}</p>
        </MoreDetails>
      </div>
    </DetailSheet>
  );
}

/** Today's path for one deposit opens the same screen with its sheet. */
export const DepositDetailV2 = DepositsV2;
